import base_url from './baseUrl';

const memoryStore = new Map();

const getStorage = () => {
  // `react-secure-storage` depends on canvas fingerprinting; in Jest/Node it can throw.
  if (typeof window === 'undefined') {
    return {
      getItem: (k) => (memoryStore.has(k) ? memoryStore.get(k) : null),
      setItem: (k, v) => memoryStore.set(k, v),
      removeItem: (k) => memoryStore.delete(k),
    };
  }

  try {
    // Lazy-load to avoid import-time crashes in non-browser test environments.
    // eslint-disable-next-line global-require
    const secureLocalStorage = require('react-secure-storage').default;
    return secureLocalStorage;
  } catch (e) {
    return window.localStorage;
  }
};

export const getAccessToken = () => getStorage().getItem('token');
export const getRefreshToken = () => getStorage().getItem('refreshToken');
export const getExpiresAt = () => {
  const v = getStorage().getItem('expiresAt');
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const setAuthTokens = ({ accessToken, refreshToken, expiresAt, user } = {}) => {
  const storage = getStorage();
  if (accessToken) storage.setItem('token', accessToken);
  if (refreshToken) storage.setItem('refreshToken', refreshToken);
  if (typeof expiresAt === 'number') storage.setItem('expiresAt', expiresAt);
  if (user !== undefined) {
    const userData = typeof user === 'string' ? user : JSON.stringify(user);
    storage.setItem('user', userData);
  }
};

export const clearAuthTokens = () => {
  const storage = getStorage();
  storage.removeItem('token');
  storage.removeItem('refreshToken');
  storage.removeItem('expiresAt');
  storage.removeItem('user');
};

let refreshInFlight = null;

export const refreshAuthToken = async () => {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('Missing refresh token');

  refreshInFlight = (async () => {
    const response = await fetch(base_url + 'auth/token/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    let json = null;
    try {
      json = await response.json();
    } catch (e) {
      json = null;
    }

    if (!response.ok || !json || json.success !== true) {
      const msg = (json && json.message) ? json.message : `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(msg);
    }

    const data = json.data || {};
    const newAccessToken = data.accessToken || data.authToken;
    const newRefreshToken = data.refreshToken || refreshToken;
    const newExpiresAt = typeof data.expiresAt === 'number' ? data.expiresAt : Number(data.expiresAt);

    if (!newAccessToken) throw new Error('Refresh succeeded but no access token returned');

    setAuthTokens({
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
      expiresAt: Number.isFinite(newExpiresAt) ? newExpiresAt : undefined,
    });

    return { accessToken: newAccessToken, refreshToken: newRefreshToken, expiresAt: newExpiresAt };
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
};
